import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from "react";

import moment from "moment-timezone";

import { v4 as uuidv4 } from "uuid";

import { format } from "date-fns";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { useUpdateListMutation } from "@/pages/Private/redux/list/list.api";
import { UpdateType } from "@/enum/list.enum";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { User } from "@/pages/Public/pages/Login/schema/login";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { List } from "../../../schema/list";
import { Schedule } from "./Schedule";
import { Sender } from "./Sender";
import { CampaignScheduleFields } from "./CampaignScheduleFields";
import { CampaignSettingsSection } from "./CampaignSettingsSection";
import { CampaignTrackingOptions } from "./CampaignTrackingOptions";
import { SameCompanyStopOption } from "./SameCompanyStopOption";
import { SenderSelector } from "./SenderSelector/SenderSelector";
import { SenderCountBadge } from "./SenderCountBadge";
import { countSelectedSenderUsers } from "../utils/countSelectedSenderUsers.util";
import { eventStopDatePrefill } from "../utils/eventStopDatePrefill.util";
import { nextDefaultCampaignName } from "../utils/nextCampaignName.util";
import { DEFAULT_CAMPAIGN_SCHEDULE } from "../utils/defaultCampaignSchedule";

export type CreateCampaignModalProps = {
	isOpened: boolean;
	handleClose: () => void;
	listData: List;
	setCurrentlySelectedCampaign: (value: string, campaignUuid: string) => void;
	/** Event end date (ISO string) to prefill the stop date when event outreach is active. */
	defaultStopCampaignAt?: string | null;
};

export const CreateCampaignModal: FunctionComponent<CreateCampaignModalProps> = ({
	isOpened,
	handleClose,
	listData,
	setCurrentlySelectedCampaign,
	defaultStopCampaignAt,
}) => {
	const { data: sendingUsers } = useGetSendersUsersQuery();

	const { user: currentUser } = useAppSelector(authSelector);
	const currentUserId = currentUser?.id;

	const [newCampaignName, setNewCampaignName] = useState<string>("");

	// Prefill the required name field with the next free "Campaign (N)" (PREC-3860) so the modal can
	// be saved without typing anything. Seeded on the open transition only — deliberately not on
	// every `campaignConfig` change, which would overwrite a name the user is in the middle of
	// typing when the list query refetches.
	useEffect(() => {
		if (isOpened) {
			setNewCampaignName(nextDefaultCampaignName(listData?.campaignConfig));
		}
	}, [isOpened]);

	const [pausePeopleFromTheSameCompany, setPausePeopleFromTheSameCompany] =
		useState<boolean>(false);
	const [trackClicks, setTrackClicks] = useState<boolean>(false);
	const [trackOpens, setTrackOpens] = useState<boolean>(false);
	const [stopCampaignAt, setStopCampaignAt] = useState<Date | null>(null);

	// Prefill the stop date with the event end date when event outreach is active (PREC-3520).
	useEffect(() => {
		if (!isOpened) {
			return;
		}

		const prefill = defaultStopCampaignAt ?? eventStopDatePrefill(listData?.event);

		if (prefill) {
			setStopCampaignAt(new Date(prefill));
		}
	}, [isOpened, defaultStopCampaignAt, listData?.event]);

	const [updateList] = useUpdateListMutation();

	const [timezone, setTimezone] = useState<string>(moment.tz.guess());

	// Every available sender (logged-in LinkedIn account + each email) is listed, but only the
	// channels of the user creating the campaign start active (PREC-2927) — the seed for the
	// editable sender selection below.
	const defaultSenders = useMemo<Sender[]>(() => {
		return (
			sendingUsers?.data?.reduce((acc: Sender[], user: User) => {
				const isCurrentUser = user.id === currentUserId;

				if (user.unipile?.isLoggedIn) {
					acc.push({
						userId: user.id,
						active: isCurrentUser,
						name: user.firstName + " " + user.lastName,
						type: "linkedin",
						emailAddress: user.email,
						emailType: "primary",
						uuid: uuidv4(),
						firstName: user.firstName,
						lastName: user.lastName,
						deactivated: false,
					});
				}

				if (user.emails?.length && user.emails.length > 0) {
					user.emails.forEach((email) => {
						acc.push({
							userId: user.id,
							active: isCurrentUser,
							name: user.firstName + " " + user.lastName,
							emailId: email.id,
							type: "email",
							emailAddress: email.email,
							emailType: email.type,
							uuid: uuidv4(),
							firstName: user.firstName,
							lastName: user.lastName,
							deactivated: false,
						});
					});
				}

				return acc;
			}, []) || []
		);
	}, [sendingUsers?.data, currentUserId]);

	const [senders, setSenders] = useState<Sender[]>([]);

	// Reset the sender selection each time the modal opens (or the available sending users change),
	// so a freshly created campaign starts with only the creating user's channels active.
	useEffect(() => {
		if (isOpened) {
			setSenders(defaultSenders);
		}
	}, [isOpened, defaultSenders]);

	// "X/Y" badge on the Senders section — distinct users with an active sender / total users.
	const senderSelection = useMemo(() => countSelectedSenderUsers(senders), [senders]);

	const [schedule, setSchedule] = useState<Schedule>(DEFAULT_CAMPAIGN_SCHEDULE);

	const errors = useMemo(() => {
		const errors: Record<string, string> = {};

		if (!newCampaignName) {
			errors.newCampaignName = "Campaign name is required";
		}

		if (!timezone) {
			errors.timezone = "Timezone is required";
		}

		if (Object.values(schedule).every((day) => !day.active)) {
			errors.schedule = "At least one day of the week must be active";
		}

		if (senders.length === 0 || senders.every((sender) => !sender.active)) {
			errors.senders = "At least one sender must be active";
		}

		return errors;
	}, [newCampaignName, timezone, schedule, senders]);

	const formErrors = useMemo(() => {
		const formErrors: ERROR_TYPE[] = [];

		if (!newCampaignName) {
			formErrors.push(ERROR_TYPE.REQUIRED);
		}

		if (!timezone) {
			formErrors.push(ERROR_TYPE.REQUIRED);
		}

		if (Object.values(schedule).every((day) => !day.active)) {
			formErrors.push(ERROR_TYPE.REQUIRED);
		}

		// Missing senders deliberately do NOT block saving (PREC-3827): accounts without a
		// connected sender can still create the campaign — it is persisted as invalid instead.
		return formErrors;
	}, [newCampaignName, timezone, schedule]);

	const handleAddCampaign = async () => {
		if (!newCampaignName) {
			return;
		}

		const newCampaignUuid = uuidv4();
		// A campaign without an active sender must never count as enrollable. The backend reads a
		// missing `isValid` as invalid too (isCampaignSendable); writing it explicitly keeps the
		// stored entry saying what the builder decided.
		const hasActiveSender = senders.some((sender) => sender.active);

		await updateList({
			id: +(listData?.id || 0),
			updateType: UpdateType.SETTINGS,
			campaignConfig: JSON.stringify({
				...listData?.campaignConfig,
				[newCampaignUuid]: {
					id: newCampaignUuid,
					name: newCampaignName,
					languages: [],
					isEventBase: false,
					eventAt: null,
					pausePeopleFromTheSameCompany,
					trackClicks,
					trackOpens,
					stopCampaignAt,
					timezone,
					schedule,
					senders,
					// New campaigns are never the list default (PREC-3860): the choice confused users at
					// creation time, so it now lives only on the campaigns table's "Make default
					// campaign" action, where the consequence is visible next to the existing default.
					isDefault: false,
					...(hasActiveSender ? {} : { isValid: false }),
				},
			}),
			campaignIdToUpdate: newCampaignUuid,
			campaignWriteOrigin: "create",
		}).unwrap();

		// Open the sequence builder for the campaign that was just created.
		setCurrentlySelectedCampaign(newCampaignName, newCampaignUuid);
		handleClose();
	};

	return (
		<Modal
			disableSave={getAllErrors(undefined, formErrors).length > 0}
			handleClose={handleClose}
			handleSave={handleAddCampaign}
			isOpened={isOpened}
			size="md"
			title="Create Campaign"
		>
			<div className="flex flex-col gap-3">
				<CampaignSettingsSection
					status={errors.newCampaignName ? "missing" : "done"}
					title="Campaign"
				>
					<div className="flex flex-col gap-4">
						<InputField
							error={errors.newCampaignName}
							handleChange={(e: ChangeEvent<HTMLInputElement>) =>
								setNewCampaignName(e.target.value)
							}
							label="Campaign Name"
							name="campaignName"
							placeholder="Campaign Name"
							required={true}
							showError={!!errors.newCampaignName}
							value={newCampaignName}
						/>
						<SameCompanyStopOption
							isChecked={pausePeopleFromTheSameCompany}
							onChange={setPausePeopleFromTheSameCompany}
						/>
						<CampaignTrackingOptions
							setTrackClicks={setTrackClicks}
							setTrackOpens={setTrackOpens}
							trackClicks={trackClicks}
							trackOpens={trackOpens}
						/>
						<InputField
							handleChange={(e: ChangeEvent<HTMLInputElement>) => {
								if (e.target.value) {
									setStopCampaignAt(new Date(e.target.value));
								} else {
									setStopCampaignAt(null);
								}
							}}
							information="Automatically stop this campaign on the selected date"
							label="Stop Campaign At"
							min={format(new Date(), "yyyy-MM-dd")}
							name="stopCampaignAt"
							type="date"
							value={stopCampaignAt ? format(stopCampaignAt, "yyyy-MM-dd") : ""}
						/>
					</div>
				</CampaignSettingsSection>

				<CampaignSettingsSection
					status={errors.timezone || errors.schedule ? "missing" : "done"}
					title="Schedule"
				>
					<CampaignScheduleFields
						schedule={schedule}
						scheduleError={errors.schedule}
						setSchedule={setSchedule}
						setTimezone={setTimezone}
						timezone={timezone}
						timezoneError={!!errors.timezone}
					/>
				</CampaignSettingsSection>

				<CampaignSettingsSection
					headerRight={
						<SenderCountBadge
							active={senderSelection.selected > 0}
							selected={senderSelection.selected}
							total={senderSelection.total}
						/>
					}
					status={errors.senders ? "missing" : "done"}
					title="Senders"
				>
					<SenderSelector
						error={!!errors.senders}
						senders={senders}
						sendingUsers={sendingUsers?.data ?? []}
						setSenders={setSenders}
					/>
					{errors.senders && (
						<>
							<div className="text-status-error text-sm mt-2">{errors.senders}</div>
							<div className="text-text-meta text-sm mt-1">
								You can still save this campaign — it will remain invalid and cannot start until a
								sender is connected and activated.
							</div>
						</>
					)}
				</CampaignSettingsSection>

				{getAllErrors(undefined, formErrors).length
					? renderErrorMessages(getAllErrors(undefined, formErrors))
					: null}
			</div>
		</Modal>
	);
};
